import React, { useEffect, useState, useRef } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon, ChevronUpIcon, ZapIcon, CircuitBoardIcon, BrainIcon, SunIcon, MoonIcon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
export const Header = ({
  onCursorChange
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const {
    theme,
    toggleTheme,
    isDark
  } = useTheme();
  const dropdownRef = useRef(null);
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const toggleDropdown = dropdown => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };
  useEffect(() => {
    const handleScroll = () => {
      // Change header style on scroll
      setScrolled(window.scrollY > 50);
      // Determine active section for navigation highlight
      const sections = ['about', 'disciplines', 'hall-of-fame', 'portfolio', 'shop', 'contact'];
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (currentSection) {
        setActiveSection(currentSection);
      } else if (window.scrollY < 100) {
        setActiveSection('');
      }
    };
    // Close dropdown when clicking outside
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Determine if a section is active based on URL or scroll position
  const isActive = sections => {
    return sections.includes(activeSection);
  };
  // Navigation data structure with dropdowns
  const navigationItems = [{
    label: 'About',
    href: '#about',
    active: isActive(['about']),
    dropdown: [{
      label: 'Overview',
      href: '#about'
    }, {
      label: 'Endorsements',
      href: '#about-endorsements'
    }, {
      label: 'Collaborators',
      href: '#about-collaborators'
    }, {
      label: 'Methodologies',
      href: '#disciplines'
    }]
  }, {
    label: 'Work',
    href: '#portfolio',
    active: isActive(['portfolio', 'disciplines', 'hall-of-fame']),
    dropdown: [{
      label: 'Portfolio',
      href: '#portfolio'
    }, {
      label: 'Disciplines',
      href: '#disciplines'
    }, {
      label: 'Hall of Fame',
      href: '#hall-of-fame'
    }]
  }, {
    label: 'Shop',
    href: '#shop',
    active: isActive(['shop']),
    dropdown: [{
      label: 'All Products',
      href: '#shop'
    }, {
      label: 'Apparel',
      href: '#shop-apparel'
    }, {
      label: 'Accessories',
      href: '#shop-accessories'
    }, {
      label: 'Featured',
      href: '#shop-featured'
    }]
  }, {
    label: 'Connect',
    href: '#contact',
    active: isActive(['contact']),
    dropdown: [{
      label: 'Contact',
      href: '#contact'
    }, {
      label: 'Media',
      href: '#media-features'
    }, {
      label: 'Companies',
      href: '#companies'
    }]
  }];
  // Animated navigation link component with dropdown support
  const NavLink = ({
    item,
    mobile = false
  }) => {
    const hasDropdown = item.dropdown !== null;
    const isItemActive = item.active;
    return <div className={`relative ${mobile ? 'w-full' : ''}`} data-id="element-898">
        <div className="flex items-center" data-id="element-899">
          <a href={hasDropdown ? '#' : item.href} className={`
              ${mobile ? '' : 'hidden md:flex'} 
              text-[#a0b8d9] hover:text-[#5ee7df] transition-all duration-300
              uppercase text-sm tracking-wider font-mono relative group
              ${isItemActive ? 'text-[#5ee7df]' : ''}
              py-2 ${hasDropdown ? 'cursor-pointer pr-8' : ''}
            `} onClick={e => {
          if (hasDropdown) {
            e.preventDefault();
            toggleDropdown(item.label);
          } else if (mobile) {
            toggleMenu();
          }
        }} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-900">
            {item.label}
            <span className={`
                absolute -bottom-1 left-0 h-px bg-[#5ee7df] transition-all duration-300
                ${isItemActive ? 'w-full' : 'w-0 group-hover:w-full'}
              `} data-id="element-901"></span>
          </a>
          {hasDropdown && <button className={`absolute right-0 top-1/2 -translate-y-1/2 text-[#a0b8d9] hover:text-[#5ee7df] transition-all duration-300 ${mobile ? '' : 'hidden md:block'}`} onClick={() => toggleDropdown(item.label)} aria-label={`Toggle ${item.label} dropdown`} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-902">
              {openDropdown === item.label ? <ChevronUpIcon size={16} data-id="element-903" /> : <ChevronDownIcon size={16} data-id="element-904" />}
            </button>}
        </div>
        {/* Dropdown menu */}
        {hasDropdown && openDropdown === item.label && <div ref={dropdownRef} className={`
              ${mobile ? 'relative mt-2 w-full' : 'absolute top-full left-0 mt-1 min-w-[180px]'} 
              glass border border-[#1a2a40]/50 rounded-md overflow-hidden z-50
            `} data-id="element-905">
            <div className="py-2" data-id="element-906">
              {item.dropdown.map((subItem, idx) => <a key={idx} href={subItem.href} className="block px-4 py-2 text-[#a0b8d9] hover:text-[#5ee7df] hover:bg-[#1a2a40]/30 transition-all duration-300 font-mono text-xs uppercase" onClick={() => {
            if (mobile) toggleMenu();
            setOpenDropdown(null);
          }} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-907">
                  {subItem.label}
                </a>)}
            </div>
            {/* Interface corners for dropdown */}
            <span className="absolute top-0 left-0 w-2 h-2 border-t border-l border-[#5ee7df]/50" data-id="element-908"></span>
            <span className="absolute top-0 right-0 w-2 h-2 border-t border-r border-[#5ee7df]/50" data-id="element-909"></span>
            <span className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-[#5ee7df]/50" data-id="element-910"></span>
            <span className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-[#5ee7df]/50" data-id="element-911"></span>
          </div>}
      </div>;
  };
  return <header className={`
        w-full fixed top-0 z-50 transition-all duration-500
        ${scrolled ? 'glass py-3 border-b border-[#1a2a40]/30' : 'bg-transparent py-5'}
      `} data-id="element-912">
      <div className="container mx-auto px-6 flex justify-between items-center" data-id="element-913">
        <a href="#" className="flex items-center group" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-914">
          {/* Circular Logo with transparent background */}
          <div className="relative w-12 h-12 mr-3 overflow-hidden" data-id="element-915">
            <svg viewBox="0 0 100 100" className="w-full h-full" data-id="element-916">
              <defs data-id="element-917">
                <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-918">
                  <stop offset="0%" stopColor="#6ff8f0" data-id="element-919" />
                  <stop offset="100%" stopColor="#2a4d7f" data-id="element-920" />
                </linearGradient>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%" data-id="element-921">
                  <feGaussianBlur stdDeviation="3" result="blur" data-id="element-922" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-923" />
                </filter>
              </defs>
              {/* Outer circle */}
              <circle cx="50" cy="50" r="40" fill="none" stroke="url(#logoGradient)" strokeWidth="2" data-id="element-924" />
              {/* Inner circle */}
              <circle cx="50" cy="50" r="30" fill="none" stroke="url(#logoGradient)" strokeWidth="1" opacity="0.6" data-id="element-925" />
              {/* Digital circuit lines */}
              <path d="M30,50 L70,50 M50,30 L50,70 M40,40 L60,60 M40,60 L60,40" stroke="url(#logoGradient)" strokeWidth="1" opacity="0.8" data-id="element-926" />
              {/* Center node */}
              <circle cx="50" cy="50" r="8" fill="url(#logoGradient)" filter="url(#glow)" data-id="element-927" />
              {/* Pulse animation */}
              <circle cx="50" cy="50" r="15" fill="none" stroke="url(#logoGradient)" strokeWidth="1" data-id="element-928">
                <animate attributeName="r" from="8" to="25" dur="2s" repeatCount="indefinite" data-id="element-929" />
                <animate attributeName="opacity" from="0.8" to="0" dur="2s" repeatCount="indefinite" data-id="element-930" />
              </circle>
            </svg>
            <div className="absolute inset-0 group-hover:bg-[#5ee7df] transition-colors duration-500 opacity-0 group-hover:opacity-20" data-id="element-931"></div>
          </div>
          <div className="relative" data-id="element-932">
            <h1 className="text-xl font-bold tracking-tight text-gradient font-heading" data-id="element-933">
              CHERYL QIAN
            </h1>
            <div className="absolute -inset-1 bg-gradient-to-r from-[#5ee7df]/20 to-[#2a4d7f]/0 opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-300" data-id="element-934"></div>
          </div>
        </a>
        {/* Desktop navigation */}
        <nav className="hidden md:flex items-center space-x-6" data-id="element-935">
          {navigationItems.map((item, index) => <NavLink key={index} item={item} data-id="element-936" />)}
          {/* Theme Toggle Button */}
          <button onClick={toggleTheme} className="p-2 rounded-full glass transition-all duration-300 hover:scale-105" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-937">
            {isDark ? <SunIcon size={18} className="text-[#5ee7df]" data-id="element-938" /> : <MoonIcon size={18} className="text-[#0095c8]" data-id="element-939" />}
          </button>
          {/* Neural Link Button */}
          <div className="relative" data-id="element-940">
            <a href="#contact" className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-md relative overflow-hidden group" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-941">
              {/* Background layers */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#5ee7df]/10 to-[#2a4d7f]/10 backdrop-blur-sm border border-[#5ee7df]/30 rounded-md" data-id="element-942"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-[#5ee7df]/5 to-[#2a4d7f]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-943"></div>
              {/* Text and icon */}
              <span className="relative z-10 flex items-center gap-2 text-[#5ee7df] font-mono text-sm uppercase tracking-wider" data-id="element-944">
                <BrainIcon size={16} className="animate-pulse" data-id="element-945" />
                <span data-id="element-946">Neural Link</span>
              </span>
              {/* Animated circuit lines */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#5ee7df] to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1500" data-id="element-947"></div>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#5ee7df] to-transparent transform translate-x-[100%] group-hover:translate-x-[-100%] transition-transform duration-1500" data-id="element-948"></div>
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-[#5ee7df] opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-949"></div>
              <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-[#5ee7df] opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-950"></div>
              <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-[#5ee7df] opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-951"></div>
              <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-[#5ee7df] opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-952"></div>
            </a>
          </div>
        </nav>
        {/* Mobile menu button */}
        <div className="md:hidden flex items-center gap-2" data-id="element-953">
          {/* Mobile Theme Toggle */}
          <button onClick={toggleTheme} className="p-2 rounded-full glass transition-all duration-300" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-954">
            {isDark ? <SunIcon size={18} className="text-[#5ee7df]" data-id="element-955" /> : <MoonIcon size={18} className="text-[#0095c8]" data-id="element-956" />}
          </button>
          <button className="p-2 text-[#e0e6f0] relative overflow-hidden group glass rounded-full" onClick={toggleMenu} aria-label="Toggle menu" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-957">
            <div className="relative z-10" data-id="element-958">
              {isMenuOpen ? <XIcon size={24} data-id="element-959" /> : <MenuIcon size={24} data-id="element-960" />}
            </div>
            <span className="absolute inset-0 bg-[#5ee7df] opacity-0 group-hover:opacity-10 transition-opacity duration-300" data-id="element-961"></span>
          </button>
        </div>
      </div>
      {/* Mobile navigation */}
      <div className={`
          md:hidden glass w-full absolute left-0 border-t border-[#1a2a40]/30
          transform transition-all duration-500 ease-in-out
          ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}
        `} data-id="element-962">
        <nav className="flex flex-col space-y-4 px-6 py-6" data-id="element-963">
          {navigationItems.map((item, index) => <NavLink key={index} item={item} mobile={true} data-id="element-964" />)}
          {/* Mobile Neural Link Button */}
          <a href="#contact" className="mt-2 py-3 px-4 bg-gradient-to-r from-[#5ee7df]/10 to-[#2a4d7f]/10 backdrop-blur-sm border border-[#5ee7df]/30 rounded-md text-[#5ee7df] font-mono text-sm uppercase tracking-wider text-center relative group overflow-hidden" onClick={() => toggleMenu()} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-965">
            <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-966">
              <BrainIcon size={16} className="animate-pulse" data-id="element-967" />
              <span data-id="element-968">Neural Link</span>
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-[#5ee7df]/5 to-[#2a4d7f]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-969"></div>
          </a>
        </nav>
      </div>
    </header>;
};